import React, { ReactNode, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { TimesheetEntry } from '../../modules/Timesheets/config';
import { entryLabel, sortDayEntries, sumHours } from '../../modules/Timesheets/timesheetDerive';
import { displayTime, formatHoursShort } from '../../utils/timesheetTime';
import './BaseTimesheetList.scss';

export interface BaseTimesheetListProps {
  entries: TimesheetEntry[];
  showRunningTotal?: boolean;
  /** Row actions, revealed on hover. Omit for a read-only list. */
  renderActions?: (entry: TimesheetEntry) => ReactNode;
  onEntryClick?: (entry: TimesheetEntry) => void;
  className?: string;
}

/**
 * Every entry of the week in one table: date, job, project, times, hours,
 * running total, notes. One layout for the employee's page and the
 * manager's read-only view.
 */
const BaseTimesheetList: React.FC<BaseTimesheetListProps> = ({
  entries,
  showRunningTotal = true,
  renderActions,
  onEntryClick,
  className = ''
}) => {
  const rows = useMemo(() => {
    // Date order, then by start time within the day, then creation.
    const byDate = new Map<string, TimesheetEntry[]>();
    for (const e of entries) byDate.set(e.date, [...(byDate.get(e.date) ?? []), e]);
    const ordered = Array.from(byDate.keys())
      .sort()
      .flatMap((d) => sortDayEntries(byDate.get(d) ?? []));
    let running = 0;
    return ordered.map((entry) => {
      running += Number(entry.hours);
      return { entry, running };
    });
  }, [entries]);

  const total = useMemo(() => sumHours(entries), [entries]);

  if (entries.length === 0) {
    return (
      <div className={`timesheet-list-wrap ${className}`.trim()}>
        <p className="ui-table__empty">No time entries for this week.</p>
      </div>
    );
  }

  let lastDate = '';

  return (
    <div className={`timesheet-list-wrap ${className}`.trim()}>
      <table className="ui-table timesheet-list">
        <thead>
          <tr>
            <th className="timesheet-list__date-col">Date</th>
            <th className="timesheet-list__code-col">Job</th>
            <th>Project</th>
            <th className="timesheet-list__time-col">Time</th>
            <th className="timesheet-list__hours-col">Hours</th>
            {showRunningTotal && <th className="timesheet-list__hours-col">Running</th>}
            <th>Notes</th>
            {renderActions && <th className="ui-table__actions" />}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ entry, running }) => {
            const label = entryLabel(entry);
            const firstOfDay = entry.date !== lastDate;
            lastDate = entry.date;
            return (
              <tr
                key={entry.id}
                className={`timesheet-list__row ${entry.leave_type ? 'is-leave' : ''} ${firstOfDay ? 'is-first-of-day' : ''} ${onEntryClick ? 'is-clickable' : ''}`}
                onClick={onEntryClick ? () => onEntryClick(entry) : undefined}
              >
                <td className="timesheet-list__date-col">
                  {firstOfDay ? (
                    <span className="timesheet-list__date">
                      <span className="timesheet-list__day">{format(parseISO(entry.date), 'EEE')}</span>
                      <span className="timesheet-list__date-num">{format(parseISO(entry.date), 'd MMM')}</span>
                    </span>
                  ) : null}
                </td>
                <td className="timesheet-list__code-col">
                  {entry.leave_type ? <span className="ui-chip">Leave</span> : <span className="ui-table__mono">{label.code ?? '—'}</span>}
                </td>
                <td>
                  <span className="timesheet-list__project">
                    <span className="timesheet-list__project-name">{label.name}</span>
                    {label.sub && <span className="timesheet-list__project-sub">{label.sub}</span>}
                  </span>
                </td>
                <td className="timesheet-list__time-col">
                  {entry.start_time && entry.end_time ? (
                    <span className="ui-table__mono">
                      {displayTime(entry.start_time)}–{displayTime(entry.end_time)}
                    </span>
                  ) : (
                    <span className="ui-table__na">–</span>
                  )}
                </td>
                <td className="timesheet-list__hours-col">
                  <span className="timesheet-list__hours">{formatHoursShort(Number(entry.hours))}</span>
                </td>
                {showRunningTotal && (
                  <td className="timesheet-list__hours-col">
                    <span className="ui-table__muted timesheet-list__running">{formatHoursShort(running)}</span>
                  </td>
                )}
                <td className="timesheet-list__notes">
                  {entry.description ? entry.description : <span className="ui-table__na">–</span>}
                </td>
                {renderActions && (
                  <td className="ui-table__actions" onClick={(e) => e.stopPropagation()}>
                    <div className="row-actions">{renderActions(entry)}</div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="timesheet-list__totals">
            <td colSpan={4} className="timesheet-list__totals-label">
              Week total
            </td>
            <td className="timesheet-list__hours-col">
              <span className="timesheet-list__hours is-grand">{formatHoursShort(total)}</span>
            </td>
            <td colSpan={(showRunningTotal ? 1 : 0) + 1 + (renderActions ? 1 : 0)} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default BaseTimesheetList;
